import type { CurationTool, CurationToolFactory, Pivot } from '@empathyco/playboard-core-domain'
import type {
  PivotableExtraParams,
  RegularFilterConfig,
  RegularFilterSelectedValues,
} from '../../models'
import type { CurationToolDomainService } from '../interfaces'
import { inject, injectable } from 'inversify'
import { TYPES } from '../../config/inversify.types'
import { FilterIdEnum } from '../../enums'
import { FiltersUtils } from '../../utils'

@injectable()
export class PlatformCurationToolDomainService implements CurationToolDomainService {
  public constructor(@inject(TYPES.CurationToolFactory) protected factory: CurationToolFactory) {}

  getEmptyInstance(): CurationTool {
    return this.factory()
  }

  setCriteria(oldCriteria: CurationTool['criteria']): CurationTool['criteria'] {
    return JSON.parse(JSON.stringify(oldCriteria))
  }

  isValid(instance: CurationTool, extraParams: PivotableExtraParams): boolean {
    return (
      !!instance.data.rules.length &&
      this.isCriteriaValid(instance.criteria, extraParams.pivotsConfig)
    )
  }

  isCriteriaValid(
    criteria: CurationTool['criteria'],
    pivotsConfig: Record<string, RegularFilterConfig>,
  ): boolean {
    return !!criteria.queries[0] && this.arePivotsValid(criteria.pivots, pivotsConfig)
  }

  arePivotsValid(pivots: Array<Pivot>, pivotsConfig: Record<string, RegularFilterConfig>): boolean {
    return Object.values(pivotsConfig).every(pivotConfig => {
      pivotConfig = {
        ...pivotConfig,
        mandatory: pivotConfig.id === FilterIdEnum.LANG,
      }
      const pivotName = FiltersUtils.getEncodedId(pivotConfig)
      const pivot = pivots.find(pivot => pivot.name === pivotName)
      return pivot ? this.isPivotValid(pivot, pivotConfig) : !pivotConfig.mandatory
    })
  }

  private isPivotValid(pivot: Pivot, pivotConfig: RegularFilterConfig): boolean {
    return (
      (!pivotConfig.mandatory || !!pivot.values.length) &&
      pivot.values.every(
        pivotValue => !!FiltersUtils.findValueConfig(pivotConfig.values, { taggingId: pivotValue }),
      )
    )
  }

  updatePivotValues(
    pivots: Array<Pivot>,
    name: string,
    values: RegularFilterSelectedValues,
  ): Array<Pivot> {
    const pivotsCopy = pivots.filter(pivots => pivots.name !== name)

    pivotsCopy.push({ name, values })

    return pivotsCopy
  }
}
